import React, { useState, useEffect, useRef, useMemo } from "react";
import { FileText, Clock, CheckCircle, UserCheck, Search as SearchIcon, Send, ChevronDown, Check, AlertCircle, X, Eye, ChevronLeft, ChevronRight, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import Header from "./Header";
import { fetchPeerReviews, fetchEligibleManuscripts, submitPeerReview, cancelPeerReview } from "../service/PeerReviewService";
import { calculateOverallScore } from "../service/Operations";

const statusConfig = {
    awaitingReviewer: { label: "Awaiting Reviewer", icon: Clock, cls: "badge-pr-awaiting" },
    assigned:         { label: "Assigned",          icon: UserCheck, cls: "badge-pr-assigned" },
    inReview:         { label: "In Review",         icon: Eye, cls: "badge-pr-inreview" },
    reviewCompleted:  { label: "Completed",         icon: CheckCircle, cls: "badge-pr-completed" },
};

function PeerReviewBadge({ status }) {
    const cfg = statusConfig[status] || statusConfig.awaitingReviewer;
    const Icon = cfg.icon;
    return (
        <span className={`status-badge ${cfg.cls}`}>
            <Icon size={12} />
            {cfg.label}
        </span>
    );
}

function pageWindows(current, total) {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const show = new Set([1, total, current - 1, current, current + 1].filter(n => n >= 1 && n <= total));
    const sorted = [...show].sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
        if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null);
        result.push(sorted[i]);
    }
    return result;
}

export default function PeerReviews() {
    const navigate = useNavigate();
    const [reviews, setReviews] = useState([]);
    const [eligible, setEligible] = useState([]);
    const [loading, setLoading] = useState(true);
    const [eligibleLoading, setEligibleLoading] = useState(true);

    // Filters
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [statusOpen, setStatusOpen] = useState(false);
    const statusRef = useRef(null);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 10;

    // Submit modal
    const [submitModal, setSubmitModal] = useState(false);
    const [selectedManuscript, setSelectedManuscript] = useState(null);
    const [submitNotes, setSubmitNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);

    // Cancel
    const [cancelling, setCancelling] = useState(null);

    // Active tab
    const [activeTab, setActiveTab] = useState("reviews");

    useEffect(() => {
        fetchPeerReviews()
            .then(res => setReviews(res.reviews ?? []))
            .catch(() => {})
            .finally(() => setLoading(false));
        fetchEligibleManuscripts()
            .then(res => setEligible(res.manuscripts ?? []))
            .catch(() => {})
            .finally(() => setEligibleLoading(false));
    }, []);

    useEffect(() => {
        const onDocClick = (e) => {
            if (statusRef.current && !statusRef.current.contains(e.target)) setStatusOpen(false);
        };
        document.addEventListener("click", onDocClick);
        return () => document.removeEventListener("click", onDocClick);
    }, []);

    useEffect(() => { setPage(1); }, [search, statusFilter, activeTab]);

    const filteredReviews = useMemo(() => {
        let list = [...reviews].sort((a, b) => (b.submittedDate ?? 0) - (a.submittedDate ?? 0));
        if (search) {
            const q = search.toLowerCase();
            list = list.filter(r =>
                (r.manuscriptTitle ?? "").toLowerCase().includes(q) ||
                (r.manuscriptAuthors ?? "").toLowerCase().includes(q) ||
                (r.reviewerName ?? "").toLowerCase().includes(q)
            );
        }
        if (statusFilter !== "ALL") {
            list = list.filter(r => r.status === statusFilter);
        }
        return list;
    }, [reviews, search, statusFilter]);

    const filteredEligible = useMemo(() => {
        let list = [...eligible].sort((a, b) => (b.uploadDate ?? 0) - (a.uploadDate ?? 0));
        if (search) {
            const q = search.toLowerCase();
            list = list.filter(m =>
                (m.title ?? "").toLowerCase().includes(q) ||
                (m.authors ?? "").toLowerCase().includes(q)
            );
        }
        return list;
    }, [eligible, search]);

    const activeList = activeTab === "reviews" ? filteredReviews : filteredEligible;
    const totalPages = Math.max(1, Math.ceil(activeList.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const paginated = activeList.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

    const handleSubmit = async (manuscriptId) => {
        setSubmitting(true);
        setSubmitStatus(null);
        try {
            const res = await submitPeerReview(manuscriptId, submitNotes || null);
            if (res.success) {
                setSubmitStatus({ type: "success", msg: "Paper submitted for peer review." });
                setSubmitNotes("");
                // Refresh data
                const [revRes, eligRes] = await Promise.all([fetchPeerReviews(), fetchEligibleManuscripts()]);
                setReviews(revRes.reviews ?? []);
                setEligible(eligRes.manuscripts ?? []);
                setTimeout(() => { setSubmitModal(false); setSubmitStatus(null); setSelectedManuscript(null); }, 1500);
            } else {
                setSubmitStatus({ type: "error", msg: res.message || "Failed to submit. Please try again." });
            }
        } catch {
            setSubmitStatus({ type: "error", msg: "Failed to submit. Please try again." });
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = async (reviewId) => {
        if (!window.confirm("Cancel this peer review request?")) return;
        setCancelling(reviewId);
        try {
            const res = await cancelPeerReview(reviewId);
            if (res.success) {
                const [revRes, eligRes] = await Promise.all([fetchPeerReviews(), fetchEligibleManuscripts()]);
                setReviews(revRes.reviews ?? []);
                setEligible(eligRes.manuscripts ?? []);
            }
        } catch {}
        finally { setCancelling(null); }
    };

    const openSubmitModal = (manuscript) => {
        setSelectedManuscript(manuscript);
        setSubmitNotes("");
        setSubmitStatus(null);
        setSubmitModal(true);
    };

    const statusFilterLabel = statusFilter === "ALL" ? "All Statuses" : (statusConfig[statusFilter]?.label ?? statusFilter);

    return (
        <div className="home-layout">
            <Sidebar activePage="Peer Reviews" />

            <main className="home-main">
                <Header />

                <div className="home-content">
                    <div className="upload-main">
                        <div className="upload-page-title-row">
                            <h1 className="upload-page-heading">Peer Reviews</h1>
                        </div>

                        {/* Tabs */}
                        <div className="pr-tabs">
                            <button
                                className={`pr-tab${activeTab === "reviews" ? " pr-tab-active" : ""}`}
                                onClick={() => setActiveTab("reviews")}
                            >
                                <FileText size={14} />
                                My Reviews
                                {reviews.length > 0 && <span className="pr-tab-count">{reviews.length}</span>}
                            </button>
                            <button
                                className={`pr-tab${activeTab === "eligible" ? " pr-tab-active" : ""}`}
                                onClick={() => setActiveTab("eligible")}
                            >
                                <Send size={14} />
                                Submit for Review
                                {eligible.length > 0 && <span className="pr-tab-count">{eligible.length}</span>}
                            </button>
                        </div>

                        {/* Filters */}
                        <div className="submissions-filters">
                            {activeTab === "reviews" && (
                                <div className="similar-works-filter" ref={statusRef}>
                                    <div
                                        className="similar-works-dropdown"
                                        role="button"
                                        tabIndex={0}
                                        aria-haspopup="listbox"
                                        aria-expanded={statusOpen}
                                        onClick={() => setStatusOpen(o => !o)}
                                        onKeyDown={e => {
                                            if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setStatusOpen(o => !o); }
                                            if (e.key === "Escape") setStatusOpen(false);
                                        }}
                                    >
                                        <span className="similar-works-dropdown-label">{statusFilterLabel}</span>
                                        <ChevronDown size={14} className={`similar-works-chevron${statusOpen ? " open" : ""}`} />

                                        {statusOpen && (
                                            <ul className="similar-works-dropdown-menu" role="listbox" onClick={e => e.stopPropagation()}>
                                                {[
                                                    { val: "ALL", label: "All Statuses" },
                                                    { val: "awaitingReviewer", label: "Awaiting Reviewer" },
                                                    { val: "assigned", label: "Assigned" },
                                                    { val: "inReview", label: "In Review" },
                                                    { val: "reviewCompleted", label: "Completed" },
                                                ].map(opt => (
                                                    <li
                                                        key={opt.val}
                                                        role="option"
                                                        aria-selected={statusFilter === opt.val}
                                                        className={`similar-works-dropdown-item${statusFilter === opt.val ? " active" : ""}`}
                                                        onClick={() => { setStatusFilter(opt.val); setStatusOpen(false); }}
                                                    >
                                                        <span>{opt.label}</span>
                                                        {statusFilter === opt.val && <Check size={13} />}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            )}
                            <div className="submissions-search-wrap" style={{ position: "relative", marginLeft: activeTab === "eligible" ? 0 : "auto" }}>
                                <SearchIcon size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--ink-300)", pointerEvents: "none" }} />
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    style={{ paddingLeft: 30, paddingRight: search ? 26 : 10, paddingTop: 7, paddingBottom: 7, border: "1px solid var(--border)", borderRadius: 8, fontSize: 13, background: "var(--surface-2)", outline: "none", width: 160 }}
                                />
                                {search && (
                                    <button onClick={() => setSearch("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--ink-300)", fontSize: 16, lineHeight: 1 }}>x</button>
                                )}
                            </div>
                        </div>

                        {/* Pagination */}
                        {!loading && !eligibleLoading && totalPages > 1 && (
                            <div className="pagination-bar" style={{ marginBottom: 8 }}>
                                <p className="pagination-count">{(safePage - 1) * PAGE_SIZE + 1}-{Math.min(safePage * PAGE_SIZE, activeList.length)} of {activeList.length}</p>
                                <div className="pagination-controls">
                                    <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1} style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid var(--border)", background: safePage === 1 ? "var(--surface-3)" : "var(--surface)", cursor: safePage === 1 ? "default" : "pointer", fontSize: 12, color: safePage === 1 ? "var(--ink-200)" : "var(--ink-700)", display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}><ChevronLeft size={13} /><span className="pagination-btn-text">Previous</span></button>
                                    <div className="pagination-pages">
                                        {pageWindows(safePage, totalPages).map((n, i) =>
                                            n === null
                                                ? <span key={`e${i}`} style={{ padding: "5px 2px", fontSize: 12, color: "var(--ink-300)" }}>...</span>
                                                : <button key={n} onClick={() => setPage(n)} style={{ padding: "5px 10px", borderRadius: 7, border: "1px solid var(--border)", background: n === safePage ? "var(--brand)" : "var(--surface)", color: n === safePage ? "white" : "var(--ink-700)", cursor: "pointer", fontSize: 12, fontWeight: n === safePage ? 600 : 400 }}>{n}</button>
                                        )}
                                    </div>
                                    <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid var(--border)", background: safePage === totalPages ? "var(--surface-3)" : "var(--surface)", cursor: safePage === totalPages ? "default" : "pointer", fontSize: 12, color: safePage === totalPages ? "var(--ink-200)" : "var(--ink-700)", display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}><span className="pagination-btn-text">Next</span><ChevronRight size={13} /></button>
                                </div>
                                <div className="pagination-spacer" />
                            </div>
                        )}

                        {/* Reviews tab content */}
                        {activeTab === "reviews" && (
                            <div className="sub-list">
                                {loading ? (
                                    Array.from({ length: 4 }).map((_, i) => (
                                        <div key={i} className="sub-card sub-card-skeleton">
                                            <div className="skeleton skeleton-icon" />
                                            <div className="sub-info">
                                                <div className="skeleton skeleton-title" />
                                                <div className="skeleton skeleton-authors" />
                                            </div>
                                            <div className="sub-meta">
                                                <div className="skeleton skeleton-date" />
                                            </div>
                                        </div>
                                    ))
                                ) : filteredReviews.length === 0 ? (
                                    <div className="submissions-empty">
                                        <Users size={40} className="submissions-empty-icon" />
                                        <p className="submissions-empty-title">{reviews.length === 0 ? "No peer reviews yet" : "No reviews match your filters"}</p>
                                        <p className="submissions-empty-sub">{reviews.length === 0 ? "Submit a paper for peer review to get started." : "Try adjusting your filters."}</p>
                                        {reviews.length === 0 && (
                                            <button className="start-analysis-btn" style={{ marginTop: 12 }} onClick={() => setActiveTab("eligible")}>
                                                Submit a Paper for Review
                                            </button>
                                        )}
                                    </div>
                                ) : paginated.map((review) => (
                                    <div key={review.id} className="sub-card" style={{ cursor: "pointer" }} onClick={() => navigate(`/submission?id=${review.manuscriptId}`)}>
                                        <div className="sub-icon">
                                            <FileText size={15} />
                                        </div>
                                        <div className="sub-info">
                                            <p className="sub-title">{review.manuscriptTitle || review.manuscriptName || "Untitled"}</p>
                                            <p className="sub-authors">{review.manuscriptAuthors || "Unknown Authors"}</p>
                                            {review.reviewerName && (
                                                <p className="pr-reviewer-line">
                                                    <UserCheck size={11} />
                                                    {review.reviewerName}{review.reviewerInstitution ? ` - ${review.reviewerInstitution}` : ""}
                                                </p>
                                            )}
                                        </div>
                                        <div className="sub-meta">
                                            <p className="sub-date">{review.submittedDate ? new Date(review.submittedDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}</p>
                                            <div className="sub-right">
                                                <PeerReviewBadge status={review.status} />
                                                {review.status !== "reviewCompleted" && (
                                                    <button
                                                        className="pr-cancel-btn"
                                                        onClick={(e) => { e.stopPropagation(); handleCancel(review.id); }}
                                                        disabled={cancelling === review.id}
                                                        title="Cancel review"
                                                    >
                                                        <X size={12} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Eligible tab content */}
                        {activeTab === "eligible" && (
                            <>
                                <p className="pr-eligible-hint">
                                    Only stored papers without an active review are eligible. Enable "Store My Manuscripts" in Settings to keep your papers available for review.
                                </p>
                                <div className="sub-list">
                                    {eligibleLoading ? (
                                        Array.from({ length: 4 }).map((_, i) => (
                                            <div key={i} className="sub-card sub-card-skeleton">
                                                <div className="skeleton skeleton-icon" />
                                                <div className="sub-info">
                                                    <div className="skeleton skeleton-title" />
                                                    <div className="skeleton skeleton-authors" />
                                                </div>
                                                <div className="sub-meta">
                                                    <div className="skeleton skeleton-date" />
                                                </div>
                                            </div>
                                        ))
                                    ) : filteredEligible.length === 0 ? (
                                        <div className="submissions-empty">
                                            <FileText size={40} className="submissions-empty-icon" />
                                            <p className="submissions-empty-title">No eligible papers</p>
                                            <p className="submissions-empty-sub">
                                                {eligible.length === 0
                                                    ? "Make sure \"Store My Manuscripts\" is enabled in Settings, then upload and submit papers here for review."
                                                    : "No papers match your search."}
                                            </p>
                                            {eligible.length === 0 && (
                                                <button className="start-analysis-btn" style={{ marginTop: 12 }} onClick={() => navigate("/upload")}>
                                                    Upload a Paper
                                                </button>
                                            )}
                                        </div>
                                    ) : paginated.map((m) => {
                                        const score = calculateOverallScore(m);
                                        return (
                                            <div key={m.id} className="sub-card pr-eligible-card">
                                                <div className="sub-icon">
                                                    <FileText size={15} />
                                                </div>
                                                <div className="sub-info">
                                                    <p className="sub-title">{m.title || "Untitled"}</p>
                                                    <p className="sub-authors">{m.authors || "Unknown Authors"}</p>
                                                    {m.status === "completed" && score > 0 && (
                                                        <div className="sub-scores-row">
                                                            {m.noveltyScore != null && <span className="sub-score-chip sub-score-chip-n">N: {m.noveltyScore}</span>}
                                                            {m.methodScore != null && <span className="sub-score-chip sub-score-chip-m">M: {m.methodScore}</span>}
                                                            {m.impactScore != null && <span className="sub-score-chip sub-score-chip-i">I: {m.impactScore}</span>}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="sub-meta">
                                                    <p className="sub-date">{m.uploadDate ? new Date(m.uploadDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}</p>
                                                    <button
                                                        className="pr-submit-btn"
                                                        onClick={(e) => { e.stopPropagation(); openSubmitModal(m); }}
                                                    >
                                                        <Send size={12} />
                                                        Submit for Review
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </main>

            {/* Submit modal */}
            {submitModal && selectedManuscript && (
                <div className="share-modal-overlay" onClick={() => { if (!submitting) { setSubmitModal(false); setSelectedManuscript(null); } }}>
                    <div className="share-modal-tile pr-submit-modal" onClick={e => e.stopPropagation()}>
                        <div className="share-modal-header">
                            <p className="share-modal-title">Submit for Peer Review</p>
                            <button className="share-modal-close" onClick={() => { if (!submitting) { setSubmitModal(false); setSelectedManuscript(null); } }}>x</button>
                        </div>
                        <div className="share-modal-body">
                            <div className="pr-submit-paper-info">
                                <FileText size={16} color="var(--brand-text)" />
                                <div>
                                    <p className="pr-submit-paper-title">{selectedManuscript.title || "Untitled"}</p>
                                    {selectedManuscript.authors && <p className="pr-submit-paper-authors">{selectedManuscript.authors}</p>}
                                </div>
                            </div>
                            <p className="pr-submit-desc">
                                Your paper will be added to the review queue and assigned to a qualified reviewer. You'll be notified of status updates.
                            </p>
                            {submitStatus && (
                                <p className={submitStatus.type === "success" ? "chpw-success" : "chpw-error"} style={{ marginBottom: 12 }}>
                                    {submitStatus.msg}
                                </p>
                            )}
                            <label className="upload-label">Notes for Reviewer <span style={{ color: "var(--ink-300)", fontWeight: 400 }}>(optional)</span></label>
                            <textarea
                                className="pr-submit-notes"
                                placeholder="Any specific areas you'd like the reviewer to focus on..."
                                value={submitNotes}
                                onChange={e => setSubmitNotes(e.target.value)}
                                disabled={submitting}
                                rows={3}
                            />
                            <button
                                className="auth-submit-btn"
                                disabled={submitting}
                                style={{ marginTop: 12 }}
                                onClick={() => handleSubmit(selectedManuscript.id)}
                            >
                                {submitting ? "Submitting..." : "Submit for Peer Review"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
